import React, { useEffect, useState } from 'react';
import { getTodayReservationsByLocalIdAndDateRange } from '../../../../../../crud/clients_api/client_reservations';
import Table from './components/Table';
import DateFilter from '../../../../Caja/Receipts/components/Filters/DateFilter';
import MultiSelectFilter from '../../../../../../components/common/MultiSelectFilter/MultiSelectFilter';
import CheckboxMultiSelect from '../../../../../../components/common/CheckboxMultiSelect/CheckboxMultiSelect';
import CustomDatePicker from '../../../../../../components/common/CustomDatePicker/CustomDatePicker';
import { Button, Offcanvas } from 'react-bootstrap';
import { FaSearch } from 'react-icons/fa';
import { X } from 'react-bootstrap-icons';
import { useNotifications } from '../../../../../Base/components/NavBar/components/HeaderProfile/components/NotificationCenter/NotificationContext';
import { LocalSession } from '../../../../../../types/LocalSession';
import MiniLoadingSpinner from '../../../../../../components/Spinners/MiniLoadingSpinner';

interface ClientReservation {
  reservation_id: number;
  client_id: number | null;
  client_name: string;
  email: string;
  phone_number: string;
  reservation_date: string;
  local_id: number;
  init: number;
  end: number;
  n_people: number;
  mesa_id: number;
  mesa_num: number;
  tag?: string;
  description: string;
  confirmed: boolean;
  approved: boolean;
  attended: boolean;
  cancelled: boolean;
  arrived: boolean;
}

interface ReservationsTableProps {
  lastSession?: LocalSession | null;
}

const ReservationsTable: React.FC<ReservationsTableProps> = ({ lastSession }) => {
  const { reservations, setReservations } = useNotifications();
  const [filteredReservations, setFilteredReservations] = useState<ClientReservation[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [loading, setLoading] = useState(false);

  const today = new Date().toISOString().slice(0, 10);
  const addDays = (dateStr: string, days: number) => {
    const date = new Date(dateStr);
    date.setDate(date.getDate() + days);
    return date.toISOString().slice(0, 10);
  };
  const [startDate, setStartDate] = useState<string>(addDays(today, -3));
  const [endDate, setEndDate] = useState<string>(addDays(today, 3));

  const [selectedStatuses, setSelectedStatuses] = useState<{value: string, label: string}[]>([]);
  const [selectedMesas, setSelectedMesas] = useState<{value: string, label: string}[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isMobile, setIsMobile] = useState(false);

  const statusOptions = [
    { value: 'Cancelada', label: 'Cancelada' },
    { value: 'Atendida', label: 'Atendida' },
    { value: 'Confirmada', label: 'Confirmada' },
    { value: 'Pendiente', label: 'Pendiente' },
    { value: 'No Aprobada', label: 'No Aprobada' },
  ];

  const mesaOptions = React.useMemo(() => {
    const uniqueMesas = new Map<string, string>();
    reservations.forEach((res: any) => {
      if (res.mesa_num !== undefined && res.mesa_num !== null) {
        const mesaKey = res.tag || res.mesa_num.toString();
        const mesaLabel = res.tag || `Mesa ${res.mesa_num}`;
        uniqueMesas.set(mesaKey, mesaLabel);
      }
    });
    return Array.from(uniqueMesas.entries()).map(([value, label]) => ({ value, label }));
  }, [reservations]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    let localId = localStorage.getItem('localId');
    if (!localId) {
      console.warn('El localId no fue encontrado en el localStorage, asignando 1 por defecto');
      localId = '1';
    }
    setLoading(true);
    getTodayReservationsByLocalIdAndDateRange(Number(localId), startDate || '', endDate || '')
      .then(response => {
        const mappedData = response.data.map((reservation: any) => ({
          ...reservation,
          init: reservation.reservation_init ?? reservation.init,
          end: reservation.reservation_end ?? reservation.end,
        }));
        setReservations(mappedData.reverse());
      })
      .catch(error => {
        console.error('Error al obtener las reservaciones de la API:', error);
      })
      .finally(() => setLoading(false));
  }, [startDate, endDate]);

  const slotToTime = (slot: number) => {
    const hours = Math.floor(slot / 4);
    const minutes = (slot % 4) * 15;
    const ampm = hours < 12 ? 'AM' : 'PM';
    return `${hours % 12 || 12}:${minutes.toString().padStart(2, '0')} ${ampm}`;
  };

  useEffect(() => {
    const statusMap: Record<string, (r: ClientReservation) => boolean> = {
      'Cancelada': (r) => r.cancelled,
      'Atendida': (r) => r.attended && r.approved && r.confirmed,
      'Confirmada': (r) => r.confirmed && !r.attended,
      'Pendiente': (r) => r.approved && !r.confirmed && !r.cancelled,
      'No Aprobada': (r) => !r.approved && !r.confirmed && !r.cancelled,
    };

    let filtered = reservations as ClientReservation[];

    if (selectedStatuses.length > 0) {
      filtered = filtered.filter(r =>
        selectedStatuses.some(s => {
          const checker = statusMap[s.value];
          return checker ? checker(r) : false;
        })
      );
    }

    if (selectedMesas.length > 0) {
      filtered = filtered.filter(r => {
        const mesaKey = r.tag || r.mesa_num.toString();
        return selectedMesas.some(m => m.value === mesaKey);
      });
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(r => {
        const timeStr = slotToTime(r.init).toLowerCase();
        return (
          r.client_name?.toLowerCase().includes(query) ||
          r.email?.toLowerCase().includes(query) ||
          r.phone_number?.toLowerCase().includes(query) ||
          r.n_people?.toString().includes(query) ||
          timeStr.includes(query) ||
          (r.tag && r.tag.toLowerCase().includes(query)) ||
          r.mesa_num?.toString().includes(query)
        );
      });
    }

    setFilteredReservations(filtered);
  }, [selectedStatuses, selectedMesas, searchQuery, reservations]);

  const handleStartDateChange = (newDate: string) => setStartDate(newDate);
  const handleEndDateChange = (newDate: string) => setEndDate(newDate);

  const renderToolbar = () => (
    <div className="mp-toolbar">
      {/* Row 1: Search + Dates + Filters */}
      <div className="flex flex-wrap items-center gap-2 lg:gap-3">
        <div className="relative flex-1 min-w-[180px] max-w-[380px]">
          <FaSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" size={13} />
          <input
            type="text"
            placeholder="Buscar por nombre, email, tel\u00e9fono..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="mp-form-input w-full"
            style={{ paddingLeft: '2rem' }}
          />
        </div>
        <CustomDatePicker value={startDate} onChange={handleStartDateChange} placeholder="Desde" />
        <CustomDatePicker value={endDate} onChange={handleEndDateChange} placeholder="Hasta" />
      </div>

      {/* Row 2: Dropdown filters */}
      <div className="flex items-center gap-2 mt-3 pt-3" style={{ borderTop: '1px solid var(--border-color)' }}>
        <span className="text-xs font-semibold uppercase tracking-wider opacity-35 mr-1">Filtrar:</span>
        <div className="min-w-[110px]">
          <CheckboxMultiSelect options={statusOptions} value={selectedStatuses} onChange={setSelectedStatuses} placeholder="Estado" />
        </div>
        <div className="min-w-[110px]">
          <CheckboxMultiSelect options={mesaOptions} value={selectedMesas} onChange={setSelectedMesas} placeholder="Mesa" />
        </div>
      </div>
    </div>
  );

  if (loading) {
    return <MiniLoadingSpinner left="50%" />;
  }

  return (
    <>
      {/* Desktop toolbar */}
      {!isMobile && renderToolbar()}

      {/* Mobile: search + filter button */}
      {isMobile && (
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <FaSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" size={13} />
            <input
              type="text"
              placeholder="Buscar..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="mp-form-input w-full"
              style={{ paddingLeft: '2rem' }}
            />
          </div>
          <button className="mp-btn-icon" onClick={() => setShowFilters(true)} title="Filtros">
            <FaSearch size={16} />
          </button>
        </div>
      )}

      {filteredReservations.length === 0 ? (
        <div className="mp-empty-state">
          <p>No se han registrado reservaciones para el periodo se\u00f1alado</p>
          <p className="text-sm opacity-60">Ajusta los filtros para visualizar reservaciones anteriores</p>
        </div>
      ) : (
        <Table
          reservations={filteredReservations}
          setReservations={setReservations}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
          startDate={startDate}
          endDate={endDate}
        />
      )}

      {/* Mobile Offcanvas */}
      <Offcanvas show={showFilters} onHide={() => setShowFilters(false)} placement="end" className="mp-modal-slider">
        <Offcanvas.Header>
          <div className="form-modal-title-container">
            <Offcanvas.Title className="form-modal-title">Opciones de filtrado</Offcanvas.Title>
          </div>
          <Button variant="none" onClick={() => setShowFilters(false)} className="modal-close-button">
            <X size={24} />
          </Button>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <div className="receipt-displayer-filter-container">
            <DateFilter
              startDate={startDate}
              setStartDate={handleStartDateChange}
              endDate={endDate}
              setEndDate={handleEndDateChange}
            />
            <div style={{ width: '100%' }}>
              <MultiSelectFilter options={statusOptions} onChange={(o: any) => setSelectedStatuses(o || [])} placeholder="Filtrar por estado" value={selectedStatuses} />
            </div>
            <div style={{ width: '100%' }}>
              <MultiSelectFilter options={mesaOptions} onChange={(o: any) => setSelectedMesas(o || [])} placeholder="Filtrar por mesa" value={selectedMesas} />
            </div>
          </div>
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
};

export default ReservationsTable;
